import { useQuery } from '@tanstack/react-query';
import { api } from '@shared/api/client';
import { TASKS } from '@shared/api/endpoints';
import type { ImpactReport as ImpactReportType } from '@tailorcast/shared-types';
import { ApiClientError } from '@shared/api/client';

interface ImpactReportProps {
  taskId: string;
}

export function ImpactReport({ taskId }: ImpactReportProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['task', taskId, 'impact'],
    queryFn: () => api.get<ImpactReportType>(TASKS.IMPACT(taskId)),
    retry: (count, err) => {
      if (err instanceof ApiClientError && err.status === 404) return false;
      return count < 1;
    },
  });

  if (isLoading) return <div className="text-sm text-gray-500">Loading impact report...</div>;

  if (error instanceof ApiClientError && error.status === 404) {
    return (
      <div className="text-sm text-gray-500">
        Re-audit pending. Impact report will be available after the scheduled re-analysis.
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-lg font-bold text-gray-900">{data.productsChanged}</div>
          <div className="text-xs text-gray-500">Products Changed</div>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-lg font-bold text-gray-900">
            {data.scoresBefore.avg.toFixed(1)} → {data.scoresAfter.avg.toFixed(1)}
          </div>
          <div className="text-xs text-gray-500">Avg Score</div>
        </div>
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="text-lg font-bold text-green-700">+{data.scoreDelta.toFixed(1)}</div>
          <div className="text-xs text-gray-500">Delta</div>
        </div>
      </div>

      {data.topImprovements.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Top Improvements</h4>
          <div className="space-y-1">
            {data.topImprovements.map((item) => (
              <div key={item.productId} className="flex justify-between text-sm">
                <span className="text-gray-700 truncate">{item.title}</span>
                <span className="text-green-600 font-medium ml-2">
                  {item.scoreBefore.toFixed(1)} → {item.scoreAfter.toFixed(1)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
